import {
  createEntityAdapter,
  createSlice,
  type EntityState,
  type PayloadAction,
  type SliceCaseReducers,
} from '@reduxjs/toolkit';
import { listSubUsers } from '../../apis/EnterpriseApiV1';
import { NOT_AUTHORIZED_USER_VIEW } from '../../const/messageCodes';
import { SubUser } from '../../models/SubUser';
import { reduxErrorHandler } from '../../utils/reduxError';
import { AppThunk } from '../store';

type subUsersState = {
  isLoading: boolean;
  error: null | string | Record<string, string>;
} & EntityState<SubUser>;

const subUsersAdaptor = createEntityAdapter<SubUser>();

export const subUsersSlice = createSlice<subUsersState, SliceCaseReducers<subUsersState>, 'subUsers'>({
  name: 'subUsers',
  initialState: subUsersAdaptor.getInitialState({
    isLoading: false,
    error: null,
  }),
  reducers: {
    subUsersRequested(state) {
      state.isLoading = true;
      state.error = null;
    },
    subUsersReceived(state, action: PayloadAction<SubUser[]>) {
      subUsersAdaptor.upsertMany(state, action.payload);
      state.isLoading = false;
      state.error = null;
    },
    errorReceived(state, action: PayloadAction<string | Record<string, string>>) {
      state.error = action.payload;
      state.isLoading = false;
    },
  },
});

export const { reducer: subUsersReducer } = subUsersSlice;

export const { subUsersRequested, subUsersReceived, errorReceived } = subUsersSlice.actions;

export const fetchSubUsers =
  (enterpriseId: number, parentId: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(subUsersRequested(undefined));
      const subUsers = await listSubUsers(enterpriseId, parentId);
      dispatch(subUsersReceived(subUsers));
    } catch (e: any) {
      return reduxErrorHandler(e, dispatch, errorReceived, NOT_AUTHORIZED_USER_VIEW);
    }
  };

export const selectByParentId = ({ entities }: subUsersState, parentId: number): SubUser[] => {
  if (!entities) {
    return [];
  }
  return Object.values(entities).filter(
    (subuser: SubUser | undefined): subuser is SubUser => subuser !== undefined && subuser.parentId === parentId,
  );
};
